import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Grid,
    Divider,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    ListSubheader,
} from "@mui/material";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { authHeader } from "../../auth";

const POLL_MS = 2000;
const AUTOSAVE_MS = 800;

const GENDER_OPTIONS = [
    { value: "M", label: "M — Hommes" },
    { value: "L", label: "L — Women (code FIS live : L)" },
    { value: "A", label: "A — Mixte" },
];

const CATEGORY_OPTIONS = ["WC", "COC", "FIS", "NJR", "NC", "CIT", "UNI", "ANC"];

// Disciplines par secteur. GS existe en alpin ET en télémark avec le même
// code FIS : une seule entrée. Ajouté le 01.10.2026 : télémark SP, CL, PS,
// PSQ. `par: true` = épreuve en duels (format <phase>), sinon <run>.
const DISCIPLINE_GROUPS = [
    {
        label: "Alpin",
        options: [
            { value: "SL", label: "SL — Slalom" },
            { value: "GS", label: "GS — Giant Slalom (alpin / télémark)" },
            { value: "SG", label: "SG — Super G" },
            { value: "DH", label: "DH — Downhill" },
            { value: "SC", label: "SC — Super Combined" },
            { value: "PGS", label: "PGS — Parallel Giant Slalom (parallèle)", par: true },
            { value: "PSL", label: "PSL — Parallel Slalom (parallèle)", par: true },
            { value: "CE", label: "CE — City Event (parallèle)", par: true },
        ],
    },
    {
        label: "Télémark",
        options: [
            { value: "SP", label: "SP — Sprint" },
            { value: "CL", label: "CL — Classic" },
            { value: "PSQ", label: "PSQ — Parallel Sprint, qualification" },
            { value: "PS", label: "PS — Parallel Sprint, finales (parallèle)", par: true },
        ],
    },
];
const ALL_DISCIPLINES = DISCIPLINE_GROUPS.flatMap((g) => g.options);

const MODE_RUN = "run";
const MODE_PAR = "par";

// Chemin HTTPS du serveur live FIS, déduit du mode (règle 321-timing,
// 01.10.2026) : /al pour toutes les courses individuelles (alpin et
// télémark, qualification PSQ comprise), /alp pour toutes les courses
// parallèles (duels).
const pathForMode = (mode) => (mode === MODE_PAR ? "/alp" : "/al");

const TRANSPORT_TCP = "tcp";
const TRANSPORT_HTTPS = "https";

// Codes de la page "Course & exports" -> codes Live FIS. PSP (Parallel
// Sprint dans raceTypes.js) -> PS.
const SWISSKI_TO_FIS_DISCIPLINE = {
    SL: "SL", GS: "GS", SG: "SG", DH: "DH", AC: "SC",
    SP: "SP", CL: "CL", PSP: "PS", PS: "PS", PSQ: "PSQ",
};
const SWISSKI_TO_FIS_GENDER = { M: "M", W: "L", A: "A" };

export default function TimingFisPanel() {
    const { eventKey } = useParams();
    const navigate = useNavigate();
    // Manche d'origine — transmise par FisStatusMini via ?run= quand on
    // ouvre ce panneau depuis le Chrono, pour que "Retour" y renvoie à la
    // bonne manche plutôt qu'à la manche courante par défaut. Ajouté le
    // 21.09.2026.
    const [searchParams] = useSearchParams();
    const originRun = searchParams.get("run");
    const [cfg, setCfg] = useState({
        // ⚠️ Corrigé le 04.09.2026 : ce champ n'existait pas du tout avant,
        // alors que timingFisRouter.js s'appuie dessus (cfg.mode === "par")
        // pour éviter d'envoyer le format <run> (individuel) sur une
        // épreuve parallèle.
        mode: MODE_RUN,
        transport: TRANSPORT_HTTPS,
        // Mode HTTPS (recommandé sur cet hébergement — voir avertissement
        // affiché en mode TCP).
        host: "livedata.fis-ski.com",
        basePath: "/al",
        // Mode TCP (protocole historique — nécessite un port sortant
        // non-standard, actuellement bloqué sur cet hébergement).
        server: "live.fis-ski.com",
        port: 1550,
        password: "",
        codex: "",
        run: 1,
        interCount: 0,
        raceinfo: { event: "", discipline: "", gender: "", category: "" },
    });
    const [status, setStatus] = useState({ connected: false, synced: false });
    const [error, setError] = useState("");
    const [info, setInfo] = useState("");
    const [messageText, setMessageText] = useState("");
    const [busy, setBusy] = useState(false);

    // --- Enregistrement de la config — ajouté le 01.10.2026 ------------
    // Avant, la config n'était enregistrée qu'au clic sur START : toute
    // saisie faite sans démarrer était perdue en quittant la page (d'où
    // "je dois souvent resaisir toutes les infos"). Désormais :
    // enregistrement automatique peu après chaque modification (dès que
    // le codex est saisi, exigé par le serveur) + bouton "Enregistrer".
    const [loaded, setLoaded] = useState(false);
    const [saveState, setSaveState] = useState("saved"); // "saved" | "dirty" | "saving" | "error"
    const skipNextAutosave = useRef(true);

    const apiFetch = useCallback(
        (url, options = {}) =>
            fetch(url, { ...options, headers: { "Content-Type": "application/json", ...authHeader(), ...(options.headers || {}) } }),
        []
    );

    useEffect(() => {
        setLoaded(false);
        apiFetch(`/api/timing/fis-config/${encodeURIComponent(eventKey)}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
                if (data) {
                    skipNextAutosave.current = true; // le chargement lui-même n'est pas une modification
                    setCfg((c) => {
                        const mode = data.mode || c.mode;
                        return {
                            ...c,
                            ...data,
                            mode,
                            // Toujours aligné sur le mode, même pour une config
                            // enregistrée avant cette règle.
                            basePath: pathForMode(mode),
                            raceinfo: { ...c.raceinfo, ...(data.raceinfo || {}) },
                        };
                    });
                }
            })
            .catch(() => setError("Impossible de charger la configuration FIS enregistrée."))
            .finally(() => setLoaded(true));
    }, [eventKey, apiFetch]);

    const saveConfig = useCallback(
        async (config) => {
            if (!String(config.codex || "").trim()) {
                setSaveState("dirty");
                return false;
            }
            setSaveState("saving");
            try {
                const res = await apiFetch(`/api/timing/fis-config/${encodeURIComponent(eventKey)}`, {
                    method: "POST",
                    body: JSON.stringify(config),
                });
                if (!res.ok) {
                    const d = await res.json().catch(() => ({}));
                    setError(d.error || `HTTP ${res.status}`);
                    setSaveState("error");
                    return false;
                }
                setSaveState("saved");
                return true;
            } catch (err) {
                setError(err.message);
                setSaveState("error");
                return false;
            }
        },
        [apiFetch, eventKey]
    );

    // Enregistrement automatique, regroupé (une requête ~0,8 s après la
    // dernière frappe).
    useEffect(() => {
        if (!loaded) return undefined;
        if (skipNextAutosave.current) {
            skipNextAutosave.current = false;
            return undefined;
        }
        setSaveState("dirty");
        const t = setTimeout(() => saveConfig(cfg), AUTOSAVE_MS);
        return () => clearTimeout(t);
    }, [cfg, loaded, saveConfig]);

    useEffect(() => {
        const poll = () =>
            apiFetch(`/api/timing/fis/${encodeURIComponent(eventKey)}/status`)
                .then((r) => (r.ok ? r.json() : null))
                .then((d) => d && setStatus(d))
                .catch(() => {});
        poll();
        const t = setInterval(poll, POLL_MS);
        return () => clearInterval(t);
    }, [eventKey, apiFetch]);

    const updateRaceinfo = (field, value) => {
        setCfg((c) => ({ ...c, raceinfo: { ...c.raceinfo, [field]: value } }));
    };

    // Choisir une discipline règle aussi le mode : duels (PS, PGS, PSL,
    // CE) -> Parallèle, sinon Course individuelle (PSQ compris : la
    // qualification se court comme une manche classique). Le mode reste
    // modifiable juste en dessous.
    const updateDiscipline = (value) => {
        const def = ALL_DISCIPLINES.find((d) => d.value === value);
        const mode = def?.par ? MODE_PAR : MODE_RUN;
        setCfg((c) => ({
            ...c,
            mode,
            basePath: pathForMode(mode),
            raceinfo: { ...c.raceinfo, discipline: value },
        }));
    };

    const importFromSwissSki = async () => {
        setInfo("");
        setError("");
        const res = await apiFetch(`/api/timing/swissski-config/${encodeURIComponent(eventKey)}`);
        if (!res.ok) {
            setError("Impossible de lire la config Course & exports pour cet event.");
            return;
        }
        const swiss = await res.json();
        if (!swiss) {
            setError("Aucune config Course & exports trouvée pour cet event.");
            return;
        }
        const discipline = SWISSKI_TO_FIS_DISCIPLINE[swiss.discipline];
        const def = ALL_DISCIPLINES.find((d) => d.value === discipline);
        const importedMode = def ? (def.par ? MODE_PAR : MODE_RUN) : null;
        setCfg((c) => ({
            ...c,
            codex: c.codex || swiss.codex || "",
            mode: importedMode || c.mode,
            basePath: pathForMode(importedMode || c.mode),
            raceinfo: {
                ...c.raceinfo,
                event: swiss.event || c.raceinfo.event,
                discipline: discipline || c.raceinfo.discipline,
                gender: SWISSKI_TO_FIS_GENDER[swiss.gender] || c.raceinfo.gender,
                category: CATEGORY_OPTIONS.includes(swiss.fisCategory) ? swiss.fisCategory : c.raceinfo.category,
            },
        }));
        setInfo(
            discipline
                ? "Event, discipline, genre et catégorie importés ✓"
                : "Event importé — discipline non mappable automatiquement, à saisir manuellement."
        );
    };

    const handleResult = async (res, successMsg) => {
        if (!res.ok && res.status !== 202) {
            const d = await res.json().catch(() => ({}));
            setError(d.error || `HTTP ${res.status}`);
            setInfo("");
            return false;
        }
        const d = await res.json().catch(() => ({}));
        if (res.status === 202) {
            setError("");
            setInfo(d.error || "Connexion en cours en tâche de fond…");
        } else if (d.warning) {
            setError(d.warning);
            setInfo("");
        } else {
            setError("");
            setInfo(d.info || successMsg);
        }
        if (d.connected !== undefined) setStatus((s) => ({ ...s, ...d }));
        return res.ok;
    };

    // Bouton START/STOP unique — son comportement dépend de l'état de
    // connexion courant (status.connected), rafraîchi toutes les 2s par
    // le polling ci-dessus.
    const toggleStartStop = async () => {
        setBusy(true);
        try {
            if (status.connected) {
                const res = await apiFetch(`/api/timing/fis/${encodeURIComponent(eventKey)}/stop`, { method: "POST" });
                await handleResult(res, "Déconnecté.");
                setStatus((s) => ({ ...s, connected: false, synced: false }));
            } else {
                setError("");
                setInfo("");
                if (!String(cfg.codex || "").trim()) {
                    setError("Codex manquant.");
                    return;
                }
                if (!(await saveConfig(cfg))) return;

                const startRes = await apiFetch(`/api/timing/fis/${encodeURIComponent(eventKey)}/start`, { method: "POST" });
                await handleResult(
                    startRes,
                    cfg.mode === MODE_PAR ? "Connecté — tableau envoyé ✓" : "Connecté — raceinfo/startlist/activerun envoyés ✓"
                );
            }
        } finally {
            setBusy(false);
        }
    };

    const clear = async () => {
        const res = await apiFetch(`/api/timing/fis/${encodeURIComponent(eventKey)}/clear`, { method: "POST" });
        await handleResult(res, "Clear envoyé ✓");
    };

    // Téléchargement du fichier log persistant (message complet, pas
    // tronqué comme dans la console) — ajouté le 09.09.2026.
    const downloadDebugLog = async () => {
        setError("");
        const res = await apiFetch(`/api/timing/fis/${encodeURIComponent(eventKey)}/debug-log`);
        if (!res.ok) {
            const d = await res.json().catch(() => ({}));
            setError(d.error || `HTTP ${res.status}`);
            return;
        }
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `fis-log-${eventKey}.txt`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const clearDebugLog = async () => {
        if (!window.confirm("Vider le fichier log de cet event ? Action irréversible.")) return;
        const res = await apiFetch(`/api/timing/fis/${encodeURIComponent(eventKey)}/debug-log/clear`, { method: "POST" });
        await handleResult(res, "Log vidé ✓");
    };

    // Signale explicitement à la FIS que la manche en cours est terminée
    // (<endrun/>, mandatory selon la spec).
    const endRun = async () => {
        const res = await apiFetch(`/api/timing/fis/${encodeURIComponent(eventKey)}/endrun`, { method: "POST" });
        await handleResult(res, "Fin de manche envoyée ✓ — ouvre la manche suivante à l'écran côté chrono.");
    };

    const sendData = async () => {
        const res = await apiFetch(`/api/timing/fis/${encodeURIComponent(eventKey)}/send`, { method: "POST" });
        await handleResult(res, "Données envoyées ✓");
    };

    const sendMeteo = async () => {
        const weather = window.prompt("Météo (sun, pcl, clo, sno, rai, fog, clr) :", "sun");
        if (weather === null) return;
        const res = await apiFetch(`/api/timing/fis/${encodeURIComponent(eventKey)}/meteo`, {
            method: "POST",
            body: JSON.stringify({ weather }),
        });
        await handleResult(res, "Météo envoyée ✓");
    };

    const forceResult = async () => {
        const bib = window.prompt("Dossard à forcer :");
        if (!bib) return;
        const res = await apiFetch(
            `/api/timing/fis/${encodeURIComponent(eventKey)}/force-result/${encodeURIComponent(bib)}`,
            { method: "POST" }
        );
        await handleResult(res, `Résultat forcé pour le dossard ${bib} ✓`);
    };

    const sendMessage = async () => {
        if (!messageText.trim()) return;
        const res = await apiFetch(`/api/timing/fis/${encodeURIComponent(eventKey)}/message`, {
            method: "POST",
            body: JSON.stringify({ text: messageText.trim() }),
        });
        const ok = await handleResult(res, "Message envoyé ✓ — vérifie sur la page live FIS.");
        if (ok) setMessageText("");
    };

    const isTcp = cfg.transport === TRANSPORT_TCP;
    const saveLabel = {
        saved: "✓ Enregistré",
        dirty: cfg.codex ? "Modifications en cours…" : "Non enregistré — saisir le codex",
        saving: "Enregistrement…",
        error: "⚠ Erreur d'enregistrement",
    }[saveState];

    return (
        <Paper sx={{ p: 2, maxWidth: 480 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="h6">Serveur FIS</Typography>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                        navigate(
                            `/timingsoftware/chrono/${encodeURIComponent(eventKey)}${originRun ? `?run=${encodeURIComponent(originRun)}` : ""}`
                        )
                    }
                >
                    ← Retour au chrono
                </Button>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: status.connected ? "success.main" : "error.main" }} />
                <Typography variant="body2">{status.connected ? "Timing online" : "Serveur Déconnecté"}</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: status.synced ? "success.main" : "error.main" }} />
                <Typography variant="body2">{status.synced ? "Sync" : "No Sync."}</Typography>
            </Box>

            {status.connected && cfg.mode !== MODE_PAR && status.autoPushActive && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    ● Envoi automatique actif — les nouveaux départs, arrivées et statuts sont
                    poussés vers la FIS toutes les 2 secondes, sans action manuelle.
                </Typography>
            )}
            {status.connected && cfg.mode !== MODE_PAR && !status.autoPushActive && (
                <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 1 }}>
                    ⚠ Envoi automatique en pause après plusieurs échecs consécutifs — vérifie l'erreur
                    ci-dessous, corrige le problème, puis clique STOP puis START pour relancer.
                </Typography>
            )}

            {status.connected && status.lastError && (
                <Typography color="error" variant="caption" display="block" mb={1}>
                    {status.lastError}
                </Typography>
            )}
            {error && <Typography color="error" variant="body2" mb={1} sx={{ whiteSpace: "pre-line" }}>{error}</Typography>}
            {info && <Typography color="success.main" variant="body2" mb={1} sx={{ whiteSpace: "pre-line" }}>{info}</Typography>}

            <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                <InputLabel>Mode</InputLabel>
                <Select
                    label="Mode"
                    value={cfg.mode || MODE_RUN}
                    onChange={(e) => setCfg({ ...cfg, mode: e.target.value, basePath: pathForMode(e.target.value) })}
                >
                    <MenuItem value={MODE_RUN}>Course individuelle (format &lt;run&gt;)</MenuItem>
                    <MenuItem value={MODE_PAR}>Parallèle — duels (format &lt;phase&gt;)</MenuItem>
                </Select>
            </FormControl>

            {cfg.mode === MODE_PAR && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    En mode Parallèle, START établit la connexion puis envoie le tableau déjà connu ;
                    ensuite les duels et résultats sont poussés à chaque action du bracket.
                    « Forcer un envoi complet » renvoie tout le tableau depuis le premier tour.
                </Typography>
            )}

            <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                <InputLabel>Mode de connexion</InputLabel>
                <Select
                    label="Mode de connexion"
                    value={cfg.transport}
                    onChange={(e) => setCfg({ ...cfg, transport: e.target.value })}
                >
                    <MenuItem value={TRANSPORT_HTTPS}>HTTPS (port 443, spec officielle v1.6)</MenuItem>
                    <MenuItem value={TRANSPORT_TCP}>TCP (port 1550, protocole historique)</MenuItem>
                </Select>
            </FormControl>

            {isTcp && (
                <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 1 }}>
                    ⚠️ Le port TCP 1550 sortant est actuellement bloqué sur cet hébergement
                    (Infomaniak) — ce mode ne fonctionnera pas tant que ce point n'est pas
                    résolu côté hébergeur. Utilise le pont Python local (fis_gui.py) en
                    attendant, ou le mode HTTPS ci-dessus.
                </Typography>
            )}

            <Grid container spacing={1}>
                {isTcp ? (
                    <>
                        <Grid item xs={8}>
                            <TextField label="Server" size="small" fullWidth value={cfg.server}
                                       onChange={(e) => setCfg({ ...cfg, server: e.target.value })} />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField label="Port" size="small" fullWidth value={cfg.port}
                                       onChange={(e) => setCfg({ ...cfg, port: e.target.value })} />
                        </Grid>
                    </>
                ) : (
                    <>
                        <Grid item xs={8}>
                            <TextField label="Host" size="small" fullWidth value={cfg.host}
                                       onChange={(e) => setCfg({ ...cfg, host: e.target.value })}
                                       helperText={cfg.raceinfo.category === "WC" ? "Coupe du monde : livedatawc.fis-ski.com" : ""} />
                        </Grid>
                        <Grid item xs={4}>
                            {/* Automatique selon le mode (voir pathForMode) :
                                /al courses individuelles, /alp parallèles. */}
                            <TextField
                                label="Path"
                                size="small"
                                fullWidth
                                value={pathForMode(cfg.mode)}
                                InputProps={{ readOnly: true }}
                                helperText={cfg.mode === MODE_PAR ? "Parallèle" : "Course individuelle"}
                            />
                        </Grid>
                    </>
                )}
                <Grid item xs={4}>
                    <TextField label="Password" size="small" fullWidth type="password" value={cfg.password}
                               onChange={(e) => setCfg({ ...cfg, password: e.target.value })} />
                </Grid>
                <Grid item xs={3}>
                    <TextField label="Codex" size="small" fullWidth value={cfg.codex}
                               onChange={(e) => setCfg({ ...cfg, codex: e.target.value.trim() })} />
                </Grid>
                {cfg.mode !== MODE_PAR && (
                    <Grid item xs={2}>
                        {/* Lecture seule depuis le 06.09.2026 : la manche
                            envoyée à la FIS est TOUJOURS celle ouverte à
                            l'écran côté chrono (status.currentRun). */}
                        <TextField
                            label="Run (manche à l'écran)"
                            size="small"
                            fullWidth
                            value={status.currentRun ?? "—"}
                            InputProps={{ readOnly: true }}
                            helperText="Suit automatiquement la manche ouverte côté chrono"
                        />
                    </Grid>
                )}
                {cfg.mode !== MODE_PAR && (
                    <Grid item xs={3}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Inters</InputLabel>
                            <Select
                                label="Inters"
                                value={cfg.interCount ?? 0}
                                onChange={(e) => setCfg({ ...cfg, interCount: Number(e.target.value) })}
                            >
                                <MenuItem value={0}>0 — Aucun</MenuItem>
                                <MenuItem value={1}>1 intermédiaire</MenuItem>
                                <MenuItem value={2}>2 intermédiaires</MenuItem>
                                <MenuItem value={3}>3 intermédiaires</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                )}
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="subtitle1">Raceinfo</Typography>
                <Button size="small" onClick={importFromSwissSki}>
                    Importer depuis Course & exports
                </Button>
            </Box>

            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <TextField
                        label="Event"
                        size="small"
                        fullWidth
                        value={cfg.raceinfo.event}
                        onChange={(e) => updateRaceinfo("event", e.target.value)}
                    />
                </Grid>
                <Grid item xs={6}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Discipline</InputLabel>
                        <Select
                            label="Discipline"
                            value={cfg.raceinfo.discipline}
                            onChange={(e) => updateDiscipline(e.target.value)}
                        >
                            {DISCIPLINE_GROUPS.flatMap((g) => [
                                <ListSubheader key={`h-${g.label}`}>{g.label}</ListSubheader>,
                                ...g.options.map((o) => (
                                    <MenuItem key={`${g.label}-${o.value}`} value={o.value}>
                                        {o.label}
                                    </MenuItem>
                                )),
                            ])}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={6}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Genre</InputLabel>
                        <Select
                            label="Genre"
                            value={cfg.raceinfo.gender}
                            onChange={(e) => updateRaceinfo("gender", e.target.value)}
                        >
                            {GENDER_OPTIONS.map((o) => (
                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Catégorie</InputLabel>
                        <Select
                            label="Catégorie"
                            value={cfg.raceinfo.category}
                            onChange={(e) => updateRaceinfo("category", e.target.value)}
                        >
                            {CATEGORY_OPTIONS.map((c) => (
                                <MenuItem key={c} value={c}>{c}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.5 }}>
                <Typography
                    variant="caption"
                    color={saveState === "error" ? "error" : saveState === "saved" ? "success.main" : "text.secondary"}
                >
                    {saveLabel}
                </Typography>
                <Button size="small" variant="outlined" onClick={() => saveConfig(cfg)} disabled={saveState === "saving"}>
                    Enregistrer
                </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Test rapide bout-en-bout : un message texte apparaît directement
          sur la page live publique FIS sans dépendre du chrono/startlist. */}
            <Typography variant="subtitle1" mb={1}>Message (test rapide)</Typography>
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Texte à afficher en direct sur la page FIS"
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") sendMessage();
                    }}
                />
                <Button variant="outlined" onClick={sendMessage} disabled={!messageText.trim()}>
                    Envoyer
                </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Button variant="contained" fullWidth sx={{ mb: 1 }} onClick={sendData} disabled={!status.connected}>
                {cfg.mode === MODE_PAR
                    ? "Forcer un envoi complet (tout le tableau)"
                    : `Forcer un envoi complet (Run ${status.currentRun ?? "—"})`}
            </Button>
            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                <Button variant="outlined" fullWidth onClick={sendMeteo} disabled={!status.connected}>Meteo &gt;&gt;</Button>
                {cfg.mode !== MODE_PAR && (
                    <Button variant="outlined" fullWidth onClick={forceResult} disabled={!status.connected}>Send result (force)</Button>
                )}
            </Box>
            {cfg.mode !== MODE_PAR && (
                <Box sx={{ mb: 1 }}>
                    <Button variant="outlined" color="secondary" fullWidth onClick={endRun} disabled={!status.connected}>
                        Fin de manche (endrun)
                    </Button>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        À cliquer une fois la manche {status.currentRun ?? "—"} terminée. Ouvre ensuite la manche
                        suivante à l'écran côté chrono : elle est redéclarée automatiquement à la FIS.
                    </Typography>
                </Box>
            )}
            <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                    variant="contained"
                    color={status.connected ? "error" : "success"}
                    fullWidth
                    onClick={toggleStartStop}
                    disabled={busy}
                >
                    {status.connected ? "STOP" : "START"}
                </Button>
                <Button variant="contained" color="warning" fullWidth onClick={clear} disabled={!status.connected}>
                    CLEAR
                </Button>
            </Box>

            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                <Button variant="outlined" fullWidth onClick={downloadDebugLog}>
                    Télécharger le log
                </Button>
                <Button variant="outlined" color="secondary" fullWidth onClick={clearDebugLog}>
                    Vider le log
                </Button>
            </Box>
        </Paper>
    );
}